import { Fragment, useEffect, useState } from 'react';

import { collectionParams } from '@playbooks/api';
import { CollectionSelectCard } from '@playbooks/components/collection/collection-card';
import { useStore, useWorkspace } from '@playbooks/contexts';
import { useAction, useInit } from '@playbooks/hooks';
import { SelectCard } from '@playbooks/molecules/cards';
import { SearchForm } from '@playbooks/molecules/forms';
import { SelectList } from '@playbooks/molecules/lists';
import { Pagination } from '@playbooks/molecules/paginations';
import { Badge } from '@playbooks/ui/components/badge';
import { Button } from '@playbooks/ui/components/button';
import { DialogFooter, DialogHeader, DialogTitle } from '@playbooks/ui/components/dialog';
import { Icon } from '@playbooks/ui/components/icon';
import { logger } from '@playbooks/utils';

const CollectionFlowSelect = ({ model, selected, setSelected, onView, onClose, onNext }) => {
	const [collections, setCollections] = useState([]);
	const [params, setParams] = useState<any>({ ...collectionParams, pageSize: 10 });
	const [meta, setMeta] = useState({ page: 0, pageSize: 0, totalRecords: 0 });
	const workspace = useWorkspace();
	const store = useStore();

	// Hooks
	useEffect(() => {
		fetchData();
	}, [params]);
	useEffect(() => {
		logger.debug('collections: ', collections);
	}, [collections]);

	// Actions
	const [fetchData, loading] = useInit(async () => {
		const response = await store.query({ url: `${workspace.rootApi}/collections`, params });
		const selected = response.data.filter(v => v.bookmarks.map(r => r.id).includes(model.id));
		setCollections(response.data);
		setSelected(selected);
		setMeta(response.meta);
	});
	const [onDelete, deleteTask] = useAction(async () => {
		const response = await store.deleteRecord({ url: `${workspace.rootApi}/bookmarks/${model.id}` });
		onNext(response.data);
	});
	const [onSave, saveTask] = useAction(async () => {
		const response = await store.updateRecord({
			url: `${workspace.rootApi}/bookmarks/${model.id}/collect`,
			data: { collectionIds: selected.map(v => v.id) },
		});
		onNext(response.data);
	});

	// Methods
	const onAdd = () => {
		onView('form');
	};
	const onSelect = value => {
		if (selected.map(v => v.id).includes(value.id)) {
			return setSelected(selected.filter(v => v.id !== value.id));
		}
		return setSelected(selected.concat(value));
	};

	// Render
	return (
		<Fragment>
			<DialogHeader>
				<DialogTitle>Save to collection</DialogTitle>
			</DialogHeader>

			<div className='border-b gap-4 flex-between'>
				<SearchForm
					id='collection_search'
					placeholder='Search collections...'
					query={params.query}
					setQuery={query => setParams({ ...params, page: 0, query })}
					size='xs'
					className='grow'
				/>
				<Button size='icon' aria-label='Add' onClick={onAdd}>
					<Icon data-icon='inline-start' type='far' icon='plus' aria-hidden />
				</Button>
			</div>

			<div>
				<SelectCard
					icon='bookmark'
					title='All bookmarks'
					subtitle='Every template you save lives here.'
					badges={<Badge variant='secondary'>Always</Badge>}
					selected={model.id}
					taskRunning={deleteTask.running}
					onClick={onDelete}
					className='w-11'
				/>
				<SelectList
					data={collections}
					loading={loading}
					icon='layer-group'
					title='Collections'
					density='compact'
					variant='inset-padded'>
					{collections.map((collection, index) => (
						<div key={index}>
							<CollectionSelectCard collection={collection} selected={selected} loading={loading} onClick={onSelect} />
						</div>
					))}
				</SelectList>
			</div>

			<div className='gap-4 flex-between'>
				<Pagination meta={meta} params={params} setParams={setParams} />
			</div>

			<DialogFooter>
				<Button variant='secondary' aria-label='cancel' onClick={onClose}>
					Close
				</Button>
				<Button isLoading={saveTask.running} onClick={onSave}>
					Save
				</Button>
			</DialogFooter>
		</Fragment>
	);
};
export { CollectionFlowSelect };
